// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import TemplateContract from '../../../utils/TemplateContract';

/**
 * Represents a holder (party) with all their associated credentials.
 * Used to group credentials by holder for UI display and selection.
 */
export interface HolderWithCredentials {
  holder: string;
  credentials: TemplateContract<Credential>[];
}

/**
 * Groups credentials by holder (party).
 * This matches the Daml layer behavior which groups by holder and processes one credential per holder.
 * See: https://github.com/DACH-NY/dtcc-canton-token-bridge/blob/07a451d46ca6df8a1a51865621a64b32992fba3f/app/daml/registry/utility-registry-v0/src/main/daml/Dtc/Registry/V0/Model/Credential.daml#L78  (withCredentialsByHolderU)
 */
export const groupCredentialsByHolder = (
  credentials: TemplateContract<Credential>[],
): HolderWithCredentials[] => {
  const holderMap = new Map<string, TemplateContract<Credential>[]>();

  credentials.forEach((cred) => {
    const { holder } = cred.payload;
    const existing = holderMap.get(holder);
    if (existing) {
      existing.push(cred);
    } else {
      holderMap.set(holder, [cred]);
    }
  });

  return Array.from(holderMap.entries()).map(([holder, creds]) => ({
    holder,
    credentials: creds,
  }));
};

/**
 * Check if a credential's holder is already a member of the allow list.
 * A holder is a member if any of their credentials has the membership claim for the given allow list.
 * See: https://github.com/DACH-NY/dtcc-canton-token-bridge/blob/07a451d46ca6df8a1a51865621a64b32992fba3f/app/daml/registry/utility-registry-v0/src/main/daml/Dtc/Registry/V0/Model/Credential.daml#L69 (isMemberOfAllowList)
 */
export const isMemberOfAllowList = (
  credential: TemplateContract<Credential>,
  allowListName: string,
): boolean => {
  return credential.payload.claims.some(
    (claim) => claim.property === 'isMemberOfAllowList' && claim.value === allowListName,
  );
};
